import React from 'react';
import { Sentry } from '@/lib/sentry';

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  message?: string;
  eventId?: string;
}

export class GlobalErrorBoundary extends React.Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  state: GlobalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, message: error?.message || 'Unexpected error' };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Global error boundary caught:', error, errorInfo);

    // Report to Sentry with component stack
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });

    this.setState({ eventId });
  }

  handleDismiss = () => {
    this.setState({ hasError: false, message: undefined, eventId: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background text-foreground p-6">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full p-6 text-center">
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-4">{this.state.message}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
                onClick={this.handleDismiss}
              >
                Dismiss
              </button>
              <button
                className="px-4 py-2 rounded-md border border-border"
                onClick={this.handleReload}
              >
                Reload
              </button>
              {this.state.eventId && (
                <button
                  className="px-4 py-2 rounded-md border border-border"
                  onClick={this.handleFeedback}
                >
                  Report Issue
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const WithGlobalErrorBoundary: React.FC<GlobalErrorBoundaryProps> = ({ children }) => {
  return (
    <GlobalErrorBoundary>
      {children}
    </GlobalErrorBoundary>
  );
};
